import { useState } from "react";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";

type TargetRef = { readonly id: string; readonly name: string } | null | undefined;

export type Subrule = {
  readonly id: string;
  readonly valuePattern: string;
  readonly matchKind: string;
  readonly priority: number;
  readonly enabled: boolean;
  readonly stakeholder: TargetRef;
  readonly destinationBankAccount: TargetRef;
};

export type CaptureResolution = {
  readonly value: string;
  readonly count: number;
  readonly stakeholder: TargetRef;
  readonly destinationBankAccount: TargetRef;
};

// A subrule's chosen target: a counterparty stakeholder, an existing internal account,
// or a new internal account named on the fly. Exactly one field is set.
export type SubruleTarget = {
  stakeholderId?: string;
  destinationBankAccountId?: string;
  destinationAccountName?: string;
};

type StakeholderOption = { readonly id: string; readonly name: string };

const targetName = (ref: { stakeholder: TargetRef; destinationBankAccount: TargetRef }) =>
  ref.stakeholder?.name ?? ref.destinationBankAccount?.name ?? null;

// The capture-subrule editor on the rule-detail page. Presentational: the page owns
// the Relay mutations and passes callbacks. Each captured value maps to a target —
// a counterparty stakeholder OR an internal account (a transfer link).
export default function SubruleEditor({
  captureLiteralPrefix,
  fallbackName,
  subrules,
  resolutions,
  stakeholders,
  accounts,
  saving,
  onCreate,
  onUpdate,
  onDelete,
}: {
  captureLiteralPrefix: string | null;
  fallbackName: string | null;
  subrules: ReadonlyArray<Subrule>;
  resolutions: ReadonlyArray<CaptureResolution>;
  stakeholders: ReadonlyArray<StakeholderOption>;
  accounts: ReadonlyArray<AccountOption>;
  saving: boolean;
  onCreate: (value: string, matchKind: string, target: SubruleTarget) => void;
  onUpdate: (subruleId: string, target: SubruleTarget) => void;
  onDelete: (subruleId: string) => void;
}) {
  const [value, setValue] = useState("");
  const [matchKind, setMatchKind] = useState("EXACT");
  const [picking, setPicking] = useState(false);
  const [remapping, setRemapping] = useState<string | null>(null);

  const add = (target: SubruleTarget) => {
    setPicking(false);
    if (value.trim() === "") return;
    onCreate(value.trim(), matchKind, target);
    setValue("");
  };

  const remap = (subruleId: string, target: SubruleTarget) => {
    setRemapping(null);
    onUpdate(subruleId, target);
  };

  return (
    <div className="bg-white rounded border p-4 space-y-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold text-gray-800">Capture subrules</h2>
        {captureLiteralPrefix && (
          <span className="text-xs text-gray-400">
            mapping the value after{" "}
            <code className="font-mono bg-gray-50 rounded px-1">{captureLiteralPrefix}</code>
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Each captured value maps to a target — a counterparty or an internal account.
        Unmapped values{" "}
        {fallbackName ? (
          <>
            fall back to <span className="font-medium">{fallbackName}</span>.
          </>
        ) : (
          "are left uncategorized."
        )}
      </p>

      {/* existing subrules */}
      <div className="divide-y rounded border">
        {subrules.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-400">No subrules yet — add one below.</p>
        )}
        {subrules.map((subrule) => (
          <div key={subrule.id} className="flex items-center gap-2 px-3 py-1.5 text-sm relative">
            <code className="font-mono text-xs bg-gray-50 rounded px-1.5 py-0.5">
              {subrule.valuePattern}
            </code>
            {subrule.matchKind === "REGEX" && (
              <span className="text-[10px] uppercase text-emerald-700">regex</span>
            )}
            <span className="text-gray-400">→</span>
            <button
              type="button"
              disabled={saving}
              onClick={() => setRemapping((id) => (id === subrule.id ? null : subrule.id))}
              className="flex-1 text-left font-medium hover:underline"
            >
              {targetName(subrule) ?? "—"}
              {subrule.destinationBankAccount && (
                <span className="ml-1 text-[10px] uppercase text-gray-400">account</span>
              )}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => onDelete(subrule.id)}
              className="text-xs text-red-300 hover:text-red-600"
            >
              remove
            </button>
            {remapping === subrule.id && (
              <div className="absolute z-20 top-full left-0 mt-1 bg-white rounded border shadow-lg p-2">
                <TargetPicker
                  stakeholders={stakeholders}
                  accounts={accounts}
                  onPick={(target) => remap(subrule.id, target)}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* add form */}
      <div className="flex items-center gap-2 relative">
        <select
          value={matchKind}
          onChange={(e) => setMatchKind(e.target.value)}
          className="rounded border px-2 py-1.5 text-sm bg-white shrink-0"
        >
          <option value="EXACT">exact</option>
          <option value="REGEX">regex</option>
        </select>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="captured value (e.g. Arun BoA ########8417)"
          className="flex-1 min-w-0 rounded border px-2 py-1.5 font-mono text-xs"
        />
        <button
          type="button"
          disabled={saving || value.trim() === ""}
          onClick={() => setPicking((v) => !v)}
          className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 shrink-0"
        >
          Map to…
        </button>
        {picking && (
          <div className="absolute z-20 top-full right-0 mt-1 bg-white rounded border shadow-lg p-2">
            <TargetPicker stakeholders={stakeholders} accounts={accounts} onPick={add} />
          </div>
        )}
      </div>

      {/* capture preview */}
      {resolutions.length > 0 && (
        <div className="border-t pt-2">
          <p className="text-xs text-gray-500 uppercase mb-1">Captured values</p>
          <div className="max-h-48 overflow-y-auto rounded border divide-y">
            {resolutions.map((resolution) => (
              <div key={resolution.value} className="flex items-center gap-2 px-2 py-1 text-xs">
                <code className="font-mono">{resolution.value}</code>
                <span className="text-gray-400">×{resolution.count.toLocaleString()}</span>
                <span className="flex-1 text-right">
                  {targetName(resolution) ? (
                    targetName(resolution)
                  ) : (
                    <span className="text-orange-600">unmapped</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Pick a subrule target: a counterparty stakeholder OR an internal account (existing or
// created on the fly). A small kind toggle swaps the combobox.
function TargetPicker({
  stakeholders,
  accounts,
  onPick,
}: {
  stakeholders: ReadonlyArray<StakeholderOption>;
  accounts: ReadonlyArray<AccountOption>;
  onPick: (target: SubruleTarget) => void;
}) {
  const [kind, setKind] = useState<"STAKEHOLDER" | "ACCOUNT">("STAKEHOLDER");
  const tabClass = (active: boolean) =>
    `px-2 py-1 rounded border text-xs ${active ? "bg-emerald-600 text-white border-emerald-600" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`;

  return (
    <div className="w-72 space-y-2">
      <div className="flex gap-1">
        <button type="button" onClick={() => setKind("STAKEHOLDER")} className={tabClass(kind === "STAKEHOLDER")}>
          Counterparty
        </button>
        <button type="button" onClick={() => setKind("ACCOUNT")} className={tabClass(kind === "ACCOUNT")}>
          Internal account
        </button>
      </div>
      {kind === "STAKEHOLDER" ? (
        <StakeholderCombobox
          options={stakeholders}
          onPick={(choice) => choice.stakeholderId && onPick({ stakeholderId: choice.stakeholderId })}
        />
      ) : (
        <AccountCombobox
          options={accounts}
          onPick={(choice) =>
            onPick(
              choice.bankAccountId
                ? { destinationBankAccountId: choice.bankAccountId }
                : { destinationAccountName: choice.name }
            )
          }
        />
      )}
    </div>
  );
}
